import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronDown, ChevronRight } from 'lucide-react';

import { Card, PageHeader, TransitionLink } from '../../../components';
import { homeTiles, tileDate } from '../../home/homeTiles';
import { TREND_WINDOWS } from '../../home/trendSeries';
import { overallScore, useDiagnoses } from '../record';

import type { TrendWindow } from '../../home/trendSeries';
import type { DiagnosisRecord } from '../record';

/**
 * 진단 결과 목록(시안 `777:6586`) — 저장된 회차를 최신순으로, 회차마다 종합 점수와 지표
 * 일곱 줄. 누르면 그 회차의 상세(`/result/:id`)로 간다.
 *
 * 온보딩 feature 안에 두는 이유: 이 화면이 읽는 `DiagnosisRecord`와 저장소를 온보딩이
 * 소유한다(record/). 별도 feature로 빼면 feature 간 import가 생긴다.
 *
 * 지표 일곱 줄은 홈 타일(`homeTiles`)과 **같은 변환**을 쓴다 — 같은 회차가 홈과 목록에서
 * 다른 값을 말하면 두 지표로 읽힌다. 종합 점수도 홈 추세와 같은 함수(`overallScore`)다.
 *
 * 기간 필터(`최근 7일 ∨`)는 홈 추세와 같은 창(`TREND_WINDOWS`)을 쓴다. 필터로 비면
 * "이 기간엔 없다"고 말하고, 저장된 회차 자체가 없으면 빈 화면이 그 사실을 말한다.
 *
 * **브라우저에서는 항상 비어 있다.** mock 세션은 저장하지 않기 때문이다(diagnosisStore).
 *
 * 회차는 **구독형**(`useDiagnoses`)이다 — 부팅 직후 서버 이력 복원이 비동기로 끝나므로,
 * `useState` 한 번 읽기로는 이 화면이 열려 있는 동안 끝난 복원을 못 본다.
 */

const DAY_MS = 86_400_000;

const EmptyState = ({ filtered }: { filtered: boolean }) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
      <p className="font-semibold text-bk-900">
        {t(filtered ? 'web.history.emptyPeriod' : 'web.history.emptyTitle')}
      </p>
      {!filtered && (
        <p className="max-w-xs text-sm leading-relaxed text-bk-600">
          {t('web.history.emptyBody')}
        </p>
      )}
    </div>
  );
};

const RecordCard = ({ record }: { record: DiagnosisRecord }) => {
  const { t } = useTranslation();
  const date = tileDate(record.completedAt);
  const overall = overallScore(record);
  const tiles = homeTiles(record);

  return (
    <li>
      <TransitionLink to={`/result/${record.id}`} className="block">
        <Card className="px-4 pb-4 pt-[18px]">
          <div className="flex items-center justify-between text-[15px] tracking-[-0.075px] text-bk-600">
            <span>{t(date.key, date.params)}</span>
            <ChevronRight className="h-5 w-5" aria-hidden="true" />
          </div>
          <p className="mt-2 text-[28px] font-bold leading-none tracking-[-0.14px] text-primary tabular-nums">
            {overall == null
              ? t('web.result.pending')
              : t('web.history.score', { value: overall })}
          </p>
          <ul className="mt-4 flex flex-col gap-[14px]">
            {tiles.map(({ tab, value, valueKey, valueParams }) => {
              const measured = value !== null || valueKey !== undefined;
              return (
                <li
                  key={tab}
                  className="flex items-center justify-between gap-4"
                >
                  <span className="text-[13px] tracking-[-0.065px] text-bk-600">
                    {t(`web.result.tabs.name.${tab}`)}
                  </span>
                  <span
                    className={
                      measured
                        ? 'text-[15px] font-semibold tracking-[-0.075px] tabular-nums text-bk-900'
                        : 'text-xs text-bk-500'
                    }
                  >
                    {valueKey
                      ? t(valueKey, valueParams)
                      : (value ?? t('web.result.pending'))}
                  </span>
                </li>
              );
            })}
          </ul>
        </Card>
      </TransitionLink>
    </li>
  );
};

export const HistoryPage = () => {
  const { t } = useTranslation();
  const records = useDiagnoses();
  const [openedAt] = useState(() => Date.now());
  /**
   * 기본값은 **가장 넓은 창**이다. 이 화면의 목적이 아카이브인데 7일로 열면 2주 만에
   * 온 사용자가 "이 기간에는 회차가 없어요"를 본다(2026-09-16 실기기).
   */
  const [windowDays, setWindowDays] = useState<TrendWindow>(
    TREND_WINDOWS[TREND_WINDOWS.length - 1],
  );

  const visible = useMemo(
    () =>
      records.filter(
        (record) => openedAt - record.completedAt < windowDays * DAY_MS,
      ),
    [records, openedAt, windowDays],
  );

  return (
    <div className="flex min-h-dvh flex-col bg-background pb-10 pt-[calc(var(--safe-area-top,0px)+0.25rem)] text-foreground">
      <PageHeader title={t('web.history.title')} />

      {records.length === 0 ? (
        <EmptyState filtered={false} />
      ) : (
        <>
          <div className="flex justify-end px-[18px] pb-2 pt-4">
            <label className="relative flex min-h-[44px] items-center text-[15px] tracking-[-0.075px] text-bk-900">
              <span className="sr-only">{t('web.home.trend.periodLabel')}</span>
              <select
                value={windowDays}
                onChange={(event) =>
                  setWindowDays(Number(event.target.value) as TrendWindow)
                }
                className="appearance-none bg-transparent pr-6 text-right text-[15px] text-bk-900 outline-none"
              >
                {TREND_WINDOWS.map((days) => (
                  <option key={days} value={days}>
                    {t('web.home.trend.period', { value: days })}
                  </option>
                ))}
              </select>
              <ChevronDown
                className="pointer-events-none absolute right-0 h-5 w-5"
                aria-hidden="true"
              />
            </label>
          </div>

          {visible.length === 0 ? (
            <EmptyState filtered />
          ) : (
            <ul className="flex flex-col gap-4 px-[18px]">
              {visible.map((record) => (
                <RecordCard key={record.id} record={record} />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
};
